import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Plus, Clock, Package } from "lucide-react";
import {
  useUpdateCmmsWO, useCmmsWOPartsUsed, useCmmsWOLabor,
  useLogPartsUsed, useLogLabor,
} from "@/hooks/useCmmsWorkOrders";
import { useParts } from "@/hooks/useParts";
import type { CmmsWorkOrder, CmmsWOStatus } from "@/hooks/useCmmsWorkOrders";

const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-100 text-blue-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  on_hold: "bg-orange-100 text-orange-800",
  completed: "bg-green-100 text-green-800",
  verified: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

const PRIORITY_LABELS: Record<number, string> = { 1: "Critical", 2: "High", 3: "Medium", 4: "Low" };
const PRIORITY_COLORS: Record<number, string> = {
  1: "bg-red-100 text-red-800",
  2: "bg-orange-100 text-orange-800",
  3: "bg-blue-100 text-blue-800",
  4: "bg-gray-100 text-gray-800",
};

interface CmmsWODetailSheetProps {
  wo: CmmsWorkOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CmmsWODetailSheet({ wo: woProp, open, onOpenChange }: CmmsWODetailSheetProps) {
  const [displayWO, setDisplayWO] = useState<CmmsWorkOrder | null>(null);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  // Parts used form
  const [showAddPart, setShowAddPart] = useState(false);
  const [partId, setPartId] = useState("");
  const [partQty, setPartQty] = useState("1");

  // Labor form
  const [showAddLabor, setShowAddLabor] = useState(false);
  const [laborHours, setLaborHours] = useState("");
  const [laborDesc, setLaborDesc] = useState("");
  const [laborDate, setLaborDate] = useState(new Date().toISOString().split("T")[0]);

  const updateWO = useUpdateCmmsWO();
  const logParts = useLogPartsUsed();
  const logLabor = useLogLabor();
  const { data: partsUsed = [] } = useCmmsWOPartsUsed(displayWO?.id || null);
  const { data: laborEntries = [] } = useCmmsWOLabor(displayWO?.id || null);
  const { data: allParts = [] } = useParts();

  useEffect(() => {
    setDisplayWO(woProp);
    setIsEditingStatus(false);
    setShowAddPart(false);
    setShowAddLabor(false);
  }, [woProp]);

  const handleStatusSave = () => {
    if (!displayWO) return;
    updateWO.mutate(
      { id: displayWO.id, input: { status: newStatus as CmmsWOStatus } },
      { onSuccess: (data) => { setDisplayWO(data); setIsEditingStatus(false); } }
    );
  };

  const handleAddPart = () => {
    if (!displayWO || !partId) return;
    const part = allParts.find((p) => p.id === partId);
    logParts.mutate(
      {
        wo_id: displayWO.id,
        part_id: partId,
        quantity: parseFloat(partQty) || 1,
        unit_cost: part?.unit_cost || undefined,
      },
      { onSuccess: () => { setPartId(""); setPartQty("1"); setShowAddPart(false); } }
    );
  };

  const handleAddLabor = () => {
    if (!displayWO || !laborHours) return;
    logLabor.mutate(
      {
        wo_id: displayWO.id,
        hours: parseFloat(laborHours),
        description: laborDesc || undefined,
        work_date: laborDate,
      },
      { onSuccess: () => { setLaborHours(""); setLaborDesc(""); setShowAddLabor(false); } }
    );
  };

  if (!displayWO) return null;

  const totalLaborHours = laborEntries.reduce((sum, e) => sum + e.hours, 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-muted-foreground">{displayWO.wo_number}</span>
          </div>
          <SheetTitle className="text-xl">{displayWO.title}</SheetTitle>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={STATUS_COLORS[displayWO.status] || ""}>{displayWO.status.replace("_", " ")}</Badge>
            <Badge className={PRIORITY_COLORS[displayWO.priority] || ""}>{PRIORITY_LABELS[displayWO.priority]}</Badge>
            <Badge variant="outline">{displayWO.wo_type}</Badge>
            {!isEditingStatus ? (
              <Button variant="ghost" size="sm" onClick={() => { setNewStatus(displayWO.status); setIsEditingStatus(true); }}>
                <Pencil className="h-3 w-3" />
              </Button>
            ) : (
              <div className="flex items-center gap-1">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(["open", "in_progress", "on_hold", "completed", "verified", "cancelled"] as const).map((s) => (
                      <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleStatusSave}>Save</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setIsEditingStatus(false)}>Cancel</Button>
              </div>
            )}
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {displayWO.description && (
            <p className="text-sm text-muted-foreground">{displayWO.description}</p>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Due Date</p>
              <p>{displayWO.due_date || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Est. Hours</p>
              <p>{displayWO.estimated_hours || "—"}</p>
            </div>
          </div>

          <Separator />

          {/* Parts Used */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">Parts Used ({partsUsed.length})</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowAddPart(!showAddPart)}>
                <Plus className="h-3 w-3 mr-1" />{showAddPart ? "Cancel" : "Add Part"}
              </Button>
            </div>

            {showAddPart && (
              <div className="p-3 border rounded-lg mb-3 space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs">Part *</Label>
                  <Select value={partId} onValueChange={setPartId}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select part..." /></SelectTrigger>
                    <SelectContent>
                      {allParts.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} ({p.qty_in_stock} in stock)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Quantity</Label>
                  <Input className="h-8 text-xs" type="number" value={partQty} onChange={(e) => setPartQty(e.target.value)} />
                </div>
                <Button size="sm" onClick={handleAddPart} disabled={!partId || logParts.isPending}>
                  {logParts.isPending ? "Adding..." : "Log Part"}
                </Button>
              </div>
            )}

            {partsUsed.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Part</TableHead>
                      <TableHead className="text-xs text-right">Qty</TableHead>
                      <TableHead className="text-xs text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partsUsed.map((pu: any) => (
                      <TableRow key={pu.id}>
                        <TableCell className="text-xs">{pu.part?.name || "—"}</TableCell>
                        <TableCell className="text-xs text-right">{pu.quantity}</TableCell>
                        <TableCell className="text-xs text-right">{pu.unit_cost ? `$${pu.unit_cost.toFixed(2)}` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <Separator />

          {/* Labor */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">Labor ({totalLaborHours}h total)</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowAddLabor(!showAddLabor)}>
                <Plus className="h-3 w-3 mr-1" />{showAddLabor ? "Cancel" : "Log Time"}
              </Button>
            </div>

            {showAddLabor && (
              <div className="p-3 border rounded-lg mb-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Hours *</Label>
                    <Input className="h-8 text-xs" type="number" step="0.25" value={laborHours} onChange={(e) => setLaborHours(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Date</Label>
                    <Input className="h-8 text-xs" type="date" value={laborDate} onChange={(e) => setLaborDate(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input className="h-8 text-xs" value={laborDesc} onChange={(e) => setLaborDesc(e.target.value)} />
                </div>
                <Button size="sm" onClick={handleAddLabor} disabled={!laborHours || logLabor.isPending}>
                  {logLabor.isPending ? "Logging..." : "Log Time"}
                </Button>
              </div>
            )}

            {laborEntries.length > 0 && (
              <div className="space-y-2">
                {laborEntries.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-2 rounded-md bg-muted/50">
                    <div>
                      <p className="text-xs font-medium">{entry.hours}h — {entry.work_date}</p>
                      {entry.description && <p className="text-xs text-muted-foreground">{entry.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {displayWO.notes && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium mb-1">Notes</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{displayWO.notes}</p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
